import { geocodeSearch } from './geocoding';
import { fetchAllPickupLocations, fetchLocationsNear } from './storefront-api';
import {
  buildStoreAvailabilityMap,
  getAvailabilityStatus,
  getStoreQuantityAtLocation,
  isStoreFulfilmentEligible,
} from './availability';
import { getEnrichedVariantData } from './variant-availability';
import { requestStoreSelection } from './cart-conflict';

let shopSelectorContext = null;

function getDrawer() {
  return document.getElementById('store-locator-shop-drawer');
}

function getElements(drawer) {
  return {
    form: drawer.querySelector('.js-store-locator-shop-form'),
    input: drawer.querySelector('.js-store-locator-shop-input'),
    clearButton: drawer.querySelector('.js-store-locator-shop-clear'),
    searchButton: drawer.querySelector('.js-store-locator-shop-search'),
    results: drawer.querySelector('.js-store-locator-shop-results'),
    warning: drawer.querySelector('.js-store-locator-shop-warning'),
    loading: drawer.querySelector('.js-store-locator-shop-loading'),
    error: drawer.querySelector('.js-store-locator-shop-error'),
  };
}

function setLoading(elements, isLoading) {
  elements.loading.hidden = !isLoading;
  elements.searchButton.disabled = isLoading;
}

function setError(elements, message) {
  if (!message) {
    elements.error.hidden = true;
    elements.error.textContent = '';
    return;
  }

  elements.error.hidden = false;
  elements.error.textContent = message;
}

function setWarning(elements, message) {
  if (!message) {
    elements.warning.hidden = true;
    elements.warning.textContent = '';
    return;
  }

  elements.warning.hidden = false;
  elements.warning.textContent = message;
}

function clearResults(elements) {
  elements.results.innerHTML = '';
  elements.results.hidden = true;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getStatusIcons() {
  const drawer = getDrawer();

  return {
    available: drawer?.querySelector('.js-store-locator-icon-available')?.innerHTML.trim() ?? '',
    unavailable: drawer?.querySelector('.js-store-locator-icon-unavailable')?.innerHTML.trim() ?? '',
  };
}

function buildAvailabilityBadge(storeAvailabilityMap, location, statusIcons, tags = []) {
  if (!storeAvailabilityMap) return '';

  const storeQuantity = getStoreQuantityAtLocation(storeAvailabilityMap, location.id);
  const status = getAvailabilityStatus(storeQuantity);

  const statusLabels = {
    in_stock: 'In Stock',
    low_stock: 'Low Stock',
    unavailable: 'Unavailable',
  };
  const statusClassMap = {
    in_stock: 'store-locator-card__status--available',
    low_stock: 'store-locator-card__status--low-stock',
    unavailable: 'store-locator-card__status--unavailable',
  };
  const statusLabel = statusLabels[status] ?? 'Unavailable';
  const statusClass = statusClassMap[status] ?? statusClassMap.unavailable;
  const statusIcon = status === 'unavailable' ? statusIcons.unavailable : statusIcons.available;

  return `
    <div class="store-locator-card__status ${statusClass}">
      <span class="store-locator-card__status-icon" aria-hidden="true">${statusIcon}</span>
      <span>${statusLabel}</span>
    </div>
  `;
}

function buildStoreCard(location, storeAvailabilityMap, statusIcons, tags = []) {
  const card = document.createElement('article');
  card.className = 'store-locator-card';
  card.innerHTML = `
    <div class="store-locator-card__header">
      <h3 class="store-locator-card__name">${escapeHtml(location.name)}</h3>
      <button type="button" class="store-locator-card__select js-store-locator-shop-select" data-location-id="${escapeHtml(location.id)}">
        Select
      </button>
    </div>
    <p class="store-locator-card__address">${escapeHtml(location.address)}</p>
    ${buildAvailabilityBadge(storeAvailabilityMap, location, statusIcons, tags)}
  `;

  card.querySelector('.js-store-locator-shop-select').addEventListener('click', () => {
    requestStoreSelection(location);
  });

  return card;
}

function renderResults(elements, locations, storeAvailabilityMap, tags = []) {
  const fragment = document.createDocumentFragment();
  const statusIcons = getStatusIcons();

  locations.forEach((location) => {
    fragment.appendChild(buildStoreCard(location, storeAvailabilityMap, statusIcons, tags));
  });

  elements.results.innerHTML = '';
  elements.results.appendChild(fragment);
  elements.results.hidden = locations.length === 0;
}

async function handleSearch(drawer) {
  const elements = getElements(drawer);
  const query = elements.input.value.trim();
  const storeAvailabilityMap = shopSelectorContext?.storeAvailabilityMap ?? null;
  const tags = shopSelectorContext?.tags ?? [];

  setError(elements, null);
  setWarning(elements, null);
  clearResults(elements);

  if (!query) {
    setError(elements, 'Enter a postcode or suburb to search.');
    return;
  }

  setLoading(elements, true);

  try {
    const geocodeResult = await geocodeSearch(query);

    if (geocodeResult) {
      const locations = await fetchLocationsNear(geocodeResult);
      renderResults(elements, locations, storeAvailabilityMap, tags);
      return;
    }

    const locations = await fetchAllPickupLocations();
    renderResults(elements, locations, storeAvailabilityMap, tags);
    setWarning(
      elements,
      'We could not find that postcode or suburb. Here are all our click and collect stores.'
    );
  } catch (searchError) {
    console.error('Store locator shop search failed:', searchError);
    setError(elements, 'Something went wrong. Please try again.');
  } finally {
    setLoading(elements, false);
  }
}

function resetDrawerState(drawer) {
  const elements = getElements(drawer);
  elements.input.value = '';
  setError(elements, null);
  setWarning(elements, null);
  clearResults(elements);
  setLoading(elements, false);
}

export function initShopSelector() {
  const drawer = getDrawer();

  if (!drawer || drawer.dataset.storeLocatorShopInit) return;

  drawer.dataset.storeLocatorShopInit = 'true';
  const elements = getElements(drawer);

  elements.form.addEventListener('submit', (event) => {
    event.preventDefault();
    handleSearch(drawer);
  });

  elements.clearButton?.addEventListener('click', () => {
    elements.input.value = '';
    elements.input.focus();
    setError(elements, null);
    setWarning(elements, null);
    clearResults(elements);
  });

  drawer.addEventListener('on:store-locator-shop:after-close', () => {
    resetDrawerState(drawer);
    shopSelectorContext = null;
  });
}

export async function openShopSelector(opener, options = {}) {
  initShopSelector();

  let variantData = options.variantData ?? null;

  if (variantData?.variantId) {
    variantData = await getEnrichedVariantData(variantData);
  }

  shopSelectorContext = {
    storeAvailabilityMap: variantData?.storeAvailabilities
      ? buildStoreAvailabilityMap(variantData.storeAvailabilities)
      : null,
    tags: variantData?.tags ?? [],
  };

  getDrawer()?.open(opener);
}
